import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Role, setRole } from './roles';
import {
  IUser,
  TExchangeTokenFunction,
  TRedirectLoginFunction,
  TRedirectLogoutFunction,
  TUpdateSearchParamsFunction,
} from './types';

export const statePropName = 'auth';

export interface IAuthState {
  session?: string;
  authorized: boolean;
  user?: IUser;
  expires?: number;
  // unread status: messages
  regular: number;
  // unread status: friendship requests
  friendship: number;
  // unread status: system requests
  system: number;
}

export const initialState: IAuthState = {
  session: undefined,
  authorized: false,
  regular: 0,
  friendship: 0,
  system: 0,
};

export interface IUpdateUserPayload {
  session: string;
  expires: number;
}

export interface IRestoreAuthPayload {
  getCurrentSearchParams: () => URLSearchParams;
  exchangeAccessToken: TExchangeTokenFunction;
  updateSearchParams: TUpdateSearchParamsFunction;
  refreshToken: () => void;
  redirectLogin: TRedirectLoginFunction;
  redirectLogout: TRedirectLogoutFunction;
  // NOTE: this parameter needed to enforce redirect to sso after email validation (see BW-7118)
  skipAccessToken?: boolean;
}

export const authSlice = createSlice({
  name: statePropName,
  initialState,
  reducers: {
    restoreAuthFail: (state: IAuthState) => {
      // Reset the state to the initial state
      return initialState;
    },
    updateUserAuth: (state: IAuthState, action: PayloadAction<IUpdateUserPayload>) => {
      const { session, expires } = action.payload;
      state.session = session;
      state.expires = expires;
    },
    updateUser: (state: IAuthState, action: PayloadAction<IUser>) => {
      state.user = action.payload;
    },
    refreshUser: (state: IAuthState) => {},
    restoreAuthSuccess: (state: IAuthState) => {
      state.authorized = true;
    },
    updateMessagesSummary: (
      state: IAuthState,
      action: PayloadAction<{ regular: number; friendship: number; system?: number }>
    ) => {
      const { regular, friendship, system } = action.payload;
      state.regular = regular;
      state.friendship = friendship;
      state.system = system ?? 0;
    },
    getMessagesSummary: (state: IAuthState) => {},
    redirectToSSO: (state: IAuthState) => {},
    logout: (state: IAuthState, action: PayloadAction<string | undefined>) => {},
    authRestore: (state: IAuthState, action: PayloadAction<IRestoreAuthPayload>) => {},
    authRefresh: (state: IAuthState) => {},
    authRefreshDone: (state: IAuthState) => {},
    startTokenRefreshTask: (state: IAuthState) => {},
    refreshSessionToken: (state: IAuthState) => {},
    setUserPremium: (state: IAuthState) => {
      if (state.user) {
        const user = setRole(state.user, Role.WEBPREMIUM);
        state.user = user;
      }
    },
  },
});

export const AuthActions = authSlice.actions;
export default authSlice.reducer;
